import PageLayout from "@/components/feature/PageLayout";
import ScrollReveal from "@/components/base/ScrollReveal";
import { clinicalTeam, formatWorksWith, therapistProfilePath, supportTeam, type TeamMember } from "@/data/therapists";
import TeamPortraitStage from "@/components/base/TeamPortraitStage";

function TeamCard({ member, index }: { member: TeamMember; index: number }) {
  const isSupport = member.type === "support";
  const visibleSpecialties = member.specialties.slice(0, 3);
  const worksWithLabel = formatWorksWith(member.worksWith);

  return (
    <ScrollReveal variant="fadeUp" delay={index * 100} threshold={0.06}>
      <a
        href={therapistProfilePath(member.id)}
        className="bg-white/80 border rounded-2xl overflow-hidden flex flex-col h-full card-hover block"
        style={{ borderColor: member.accentColor + "22" }}
      >
        {/* Color accent top bar */}
        <div
          className="h-[3px] w-full shrink-0"
          style={{
            background: `linear-gradient(90deg, ${member.accentColor}, ${member.accentColor}88)`,
          }}
        />

        {/* Photo */}
        <TeamPortraitStage src={member.img} name={member.name} variant="card">

          {!isSupport && member.availability && (
            <div className="absolute top-3 right-3">
              <span
                className="font-manrope text-[10px] font-semibold px-2.5 py-1 rounded-full tracking-wide"
                style={
                  member.availability === "Accepting clients"
                    ? { background: "rgba(74,124,89,0.15)", color: "#4A7C59" }
                    : {
                        background: "rgba(43,43,43,0.10)",
                        color: "rgba(43,43,43,0.6)",
                      }
                }
              >
                {member.availability}
              </span>
            </div>
          )}
        </TeamPortraitStage>

        {/* Content */}
        <div className="p-6 flex flex-col flex-1">
          <h3 className="font-fraunces text-[#2B2B2B] text-xl font-medium mb-0.5">
            {member.name}
            {member.credentials && (
              <>
                ,&nbsp;
                <span className="font-manrope text-sm font-normal text-[#2B2B2B]/50">
                  {member.credentials}
                </span>
              </>
            )}
          </h3>

          <p
            className="font-manrope text-[13px] font-semibold mb-2"
            style={{ color: member.accentColor }}
          >
            {member.role}
          </p>

          {!isSupport && member.price && (
            <p
              className="font-manrope text-[13px] mb-2 font-semibold"
              style={{ color: member.accentColor }}
            >
              {member.price}
            </p>
          )}

          {!isSupport && visibleSpecialties.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-3">
              {visibleSpecialties.map((tag) => (
                <span
                  key={tag}
                  className="font-manrope text-[12px] font-semibold px-2.5 py-0.5 rounded-full"
                  style={{ color: member.tagColor, background: member.tagBg }}
                >
                  {tag}
                </span>
              ))}
            </div>
          )}

          {!isSupport && worksWithLabel && (
            <p className="font-manrope text-[#2B2B2B]/65 text-[12.5px] font-semibold leading-relaxed mb-3">
              Works with: {worksWithLabel}
            </p>
          )}

          <p className="font-manrope text-[#2B2B2B]/80 text-[14px] leading-relaxed mb-5 flex-1 line-clamp-4">
            {member.shortBio}
          </p>

          <span
            className="inline-flex items-center justify-center gap-2 font-manrope text-[14px] font-semibold px-5 py-3 rounded-full transition-all cursor-pointer whitespace-nowrap mt-auto"
            style={{
              border: `1.5px solid ${member.accentColor}55`,
              color: member.accentColor,
            }}
            onMouseEnter={(e) => {
              const el = e.currentTarget as HTMLElement;
              el.style.background = member.accentColor;
              el.style.color = "#fff";
              el.style.borderColor = member.accentColor;
            }}
            onMouseLeave={(e) => {
              const el = e.currentTarget as HTMLElement;
              el.style.background = "transparent";
              el.style.color = member.accentColor;
              el.style.borderColor = member.accentColor + "55";
            }}
          >
            View Profile
          </span>
        </div>
      </a>
    </ScrollReveal>
  );
}

export default function Therapists() {
  return (
    <>

      <PageLayout>
        {/* How to Choose */}
        <section className="w-full py-16 md:py-24 bg-[#FBF9F6]">
          <div className="w-full px-6 md:px-12 lg:px-20">
            <div className="max-w-3xl mx-auto text-center mb-14">
              <ScrollReveal variant="fadeIn" delay={0}>
                <p
                  className="font-manrope text-[13px] font-semibold tracking-widest uppercase mb-4"
                  style={{ color: "#7D54FC" }}
                >
                  How to Choose
                </p>
              </ScrollReveal>

              <ScrollReveal variant="fadeUp" delay={80}>
                <h1 className="font-fraunces text-[#2B2B2B] text-3xl md:text-4xl lg:text-[2.6rem] font-light leading-snug mb-6">
                  Not all therapists are the same{" "}
                  <em
                    className="not-italic font-normal"
                    style={{ color: "#7D54FC" }}
                  >
                    and that's the point
                  </em>
                </h1>
              </ScrollReveal>

              <ScrollReveal variant="fadeUp" delay={160}>
                <p className="font-manrope text-[#2B2B2B]/75 text-[15px] leading-relaxed max-w-2xl mx-auto">
                  Perspective Therapy serves children, teens, adults, couples, and groups, with in-person care in Larchmont and practice telehealth coverage in NY, NJ, and IN. Each clinician's profile shows their populations, formats, licensure-based locations, and availability; reach out if you'd like help choosing.
                </p>
              </ScrollReveal>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10 max-w-5xl mx-auto">
              {[
                {
                  icon: "ri-search-line",
                  title: "Explore Therapists",
                  body: "Read about each therapist's specialty, approach, and background.",
                },
                {
                  icon: "ri-heart-3-line",
                  title: "Find the Right Fit",
                  body: "Consider what matters most to you - specialty, style, or experience.",
                },
                {
                  icon: "ri-calendar-check-line",
                  title: "Schedule When You're Ready",
                  body: "Schedule directly online, or start with a free 15-minute consultation.",
                },
              ].map((item, index) => (
                <ScrollReveal
                  key={item.title}
                  variant="fadeUp"
                  delay={index * 100}
                >
                  <div className="flex flex-col items-center text-center">
                    <div
                      className="w-14 h-14 flex items-center justify-center rounded-full mb-5"
                      style={{ background: "rgba(125,84,252,0.10)" }}
                    >
                      <i
                        className={`${item.icon} text-xl`}
                        style={{ color: "#7D54FC" }}
                      ></i>
                    </div>
                    <h2 className="font-fraunces text-[#2B2B2B] text-lg font-medium mb-2">
                      {item.title}
                    </h2>
                    <p className="font-manrope text-[#2B2B2B]/70 text-[14px] leading-relaxed">
                      {item.body}
                    </p>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* Team Grid */}
        <section
          id="therapists"
          className="w-full pt-8 pb-14 md:pt-10 md:pb-20"
          style={{
            background:
              "linear-gradient(180deg, #F2EEE4 0%, #EEE8DA 45%, #F5F1E8 100%)",
          }}
        >
          <div className="w-full px-6 md:px-12 lg:px-20">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-7 mb-16">
              {clinicalTeam.map((member, i) => (
                <TeamCard key={member.id} member={member} index={i} />
              ))}
            </div>

            {/* Support Team */}
            <ScrollReveal variant="fadeUp" delay={0}>
              <h3 className="font-fraunces text-[#2B2B2B] text-2xl md:text-3xl font-light leading-snug mb-8">
                Support Team
              </h3>
            </ScrollReveal>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-7">
              {supportTeam.map((member, i) => (
                <TeamCard key={member.id} member={member} index={i} />
              ))}
            </div>
          </div>
        </section>
      </PageLayout>
    </>
  );
}
